// Payment receipts in the office: the "Payment saved" window right after recording a payment, the printable receipt page,
// and sending it (email with a portal link, WhatsApp, text, Telegram, copy link). "Include invoices" prints the full invoices after it.
import { useEffect, useState } from 'react';
import { api, useApi, useLocation, setQuery, navigate, dollars, type Row } from '../lib';
import { Button, Icon, Modal, Loading, ErrorBox, Link, useToast } from '../ui';
import { InvoiceSheet, ReceiptSheet } from '../invoice-sheet';
import { TgIcon, WaIcon } from '../send';

const digits = (p?: string) => { const d = String(p || '').replace(/\D/g, ''); return d.length === 10 ? '1' + d : d; };
const shareText = (d: Row, shop: Row, link: string) => {
  const r = d.receipt as Row;
  return `${shop.shop_name || 'Your repair shop'} — payment received, ${r.number}: ${dollars(Number(r.total))} (${(d.lines as Row[]).map(l => l.number).join(', ')}). Thank you! Receipt: ${link}`;
};

/** Right after a payment is recorded. */
export function PaymentSaved({ receiptNo, onClose }: { receiptNo: string; onClose: () => void }) {
  const { data, error } = useApi<Row>(`/api/receipts/${encodeURIComponent(receiptNo)}`);
  return (
    <Modal title="Payment saved" onClose={onClose}>
      {error ? <ErrorBox error={error} /> : !data ? <Loading /> : <div className="send-body">
        <div className="rc-done"><Icon name="check" size={30} /><div className="rc-big">{dollars(Number((data.receipt as Row).total))}</div>
          <p className="muted">Receipt <b>{receiptNo}</b> · {(data.customer as Row)?.name} · {(data.lines as Row[]).map(l => l.number).join(', ')}</p></div>
        <ReceiptSend d={data} onDone={onClose} />
        <div className="actions-row"><Button icon="printer" onClick={() => { onClose(); navigate(`/receipts/${encodeURIComponent(receiptNo)}?print=1`); }}>Print / PDF</Button>
          <Button onClick={() => { onClose(); navigate(`/receipts/${encodeURIComponent(receiptNo)}`); }}>Open receipt</Button><span className="spacer" /><Button kind="primary" onClick={onClose}>Done</Button></div>
      </div>}
    </Modal>
  );
}

/** Email / WhatsApp / Text / Telegram / Copy link. */
export function ReceiptSend({ d, onDone }: { d: Row; onDone?: () => void }) {
  const toast = useToast();
  const c = (d.customer || {}) as Row, shop = (d.shop || {}) as Row, no = String((d.receipt as Row).number);
  const [email, setEmail] = useState(String(c.email || '').split(/[,;\s]+/)[0] || '');
  const [busy, setBusy] = useState('');
  const ph = digits(c.phone);
  const link = async () => (await api<{ link: string }>(`/api/receipts/${encodeURIComponent(no)}/send`, { body: { linkOnly: true } })).link;
  const run = async (key: string, fn: () => Promise<void>) => { setBusy(key); try { await fn(); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(''); } };
  return (
    <div className="rc-send">
      <div className="po-send-email">
        <label className="field"><span>Email the receipt</span><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="customer@email.com" aria-label="Customer email" /></label>
        <Button kind="primary" icon="mail" busy={busy === 'mail'} disabled={!email.trim()} onClick={() => run('mail', async () => {
          const r = await api<Row>(`/api/receipts/${encodeURIComponent(no)}/send`, { body: { to: email } });
          if (r.sent) { toast(`Receipt emailed to ${(r.to as string[]).join(', ')}`); onDone?.(); }
          else { await navigator.clipboard?.writeText(r.link).catch(() => undefined); toast(`${r.reason} The link is copied.`, 'err'); }
        })}>Send by email</Button>
      </div>
      <div className="send-grid">
        <button type="button" className="send-opt" disabled={!!busy || !ph} title={ph ? '' : 'No phone on the customer'} onClick={() => run('wa', async () => { const l = await link(); window.open(`https://wa.me/${ph}?text=${encodeURIComponent(shareText(d, shop, l))}`, '_blank'); })}>
          <span className="so-ic wa"><WaIcon /></span><b>WhatsApp</b><small>{ph ? `To ${c.phone}` : 'No phone on file'}</small></button>
        <button type="button" className="send-opt" disabled={!!busy || !ph} onClick={() => run('sms', async () => { const l = await link(); location.href = `sms:+${ph}?&body=${encodeURIComponent(shareText(d, shop, l))}`; })}>
          <span className="so-ic sms"><Icon name="phone" size={22} /></span><b>Text message</b><small>{ph ? `SMS to ${c.phone}` : 'No phone on file'}</small></button>
        <button type="button" className="send-opt" disabled={!!busy} onClick={() => run('tg', async () => { const l = await link(); window.open(`https://t.me/share/url?url=${encodeURIComponent(l)}&text=${encodeURIComponent(shareText(d, shop, '').replace(/ Receipt: $/, ''))}`, '_blank'); })}>
          <span className="so-ic tg"><TgIcon /></span><b>Telegram</b><small>Pick the customer's chat</small></button>
        <button type="button" className="send-opt" disabled={!!busy} onClick={() => run('copy', async () => { const l = await link(); try { await navigator.clipboard.writeText(shareText(d, shop, l)); toast('Copied — paste it anywhere'); } catch { prompt('Copy this', shareText(d, shop, l)); } })}>
          <span className="so-ic copy"><Icon name="link" size={22} /></span><b>Copy link</b><small>Paste anywhere</small></button>
      </div>
    </div>
  );
}

/** /receipts/RCPT-1001 — printable; "Include invoices" adds each full invoice after the receipt. */
export function ReceiptPage({ no }: { no: string }) {
  const { query } = useLocation();
  const withInv = query.get('inv') !== '0'; // the invoices print with the receipt unless switched off
  const { data, error, reload } = useApi<Row>(`/api/receipts/${encodeURIComponent(no)}`);
  const [invs, setInvs] = useState<Row[] | null>(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!withInv || !data) { setInvs(null); return; }
    Promise.all((data.lines as Row[]).map(l => api<Row>(`/api/invoices/${l.invoice_id}`))).then(setInvs).catch(() => setInvs([]));
  }, [withInv, data]);
  const ready = !!data && (!withInv || invs !== null);
  useEffect(() => {
    if (ready && query.get('print') === '1') { setQuery({ print: '' }); setTimeout(() => window.print(), 400); }
  }, [ready]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const r = data.receipt as Row, c = data.customer as Row;
  return (
    <>
      <div className="page-head no-print">
        <div><Link to="/invoices" className="back"><Icon name="back" size={16} />Invoices</Link>
          <h1>{r.number}</h1><p>{c?.name} · {dollars(Number(r.total))} · {r.method}</p></div>
        <div className="actions rc-actions">
          <label className="check"><input type="checkbox" checked={withInv} onChange={e => setQuery({ inv: e.target.checked ? '' : '0' })} /> Include invoices</label>
          <Button icon="printer" onClick={() => window.print()}>Print / PDF</Button>
          <Button kind="primary" icon="send" onClick={() => setSending(true)}>Send</Button>
        </div>
      </div>
      <ReceiptSheet d={data} shop={data.shop as Row} />
      {withInv && (invs === null ? <Loading /> : <div className="rc-invoices">{invs.map(x => <InvoiceSheet key={(x.invoice as Row).id} inv={x.invoice as Row} customer={x.customer as Row} workOrder={x.workOrder as Row} shop={x.shop as Row} />)}</div>)}
      {sending && <Modal title={`Send receipt ${r.number}`} onClose={() => setSending(false)}><div className="send-body"><ReceiptSend d={data} onDone={() => setSending(false)} /></div></Modal>}
    </>
  );
}

/** A "Receipt" button for any payment (older payments get their number the first time). */
export function ReceiptButton({ p }: { p: Row }) {
  const toast = useToast();
  return <button type="button" className="linkish" onClick={async () => {
    try { const no = p.receipt_no || (await api<{ receipt_no: string }>(`/api/payments/${p.id}/receipt`, { body: {} })).receipt_no; navigate(`/receipts/${encodeURIComponent(no)}`); }
    catch (e) { toast((e as Error).message, 'err'); }
  }}>{p.receipt_no || 'Receipt'}</button>;
}
